import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import { colorMap } from '@components/ChipSelector/ChipSelectorTsoPq';
import { faChartLine, faCopy, faFileArrowDown, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, styled } from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { customSortIconAsc, customSortIconDesc } from '@pages/Masterdata/StyledGridPageComponents';
import { useLazyPostMeasurementGetCsvExportQuery } from '@redux/apiSlices/measurementApi';
import { useAppSelector } from '@redux/hooks';
import {
  insertActiveTableRow,
  measurementVisualizationTableRowProps,
  removeActiveTableRow,
  removeTableRow,
  setEditingRow,
  upsertTableRow,
} from '@redux/slices/measurementVisualizationSlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { StyledDataGrid, StyledEditIconWrapper, StyledOrderNumber } from '../styledMeasurementVisualizationPage';
import { csvParams, downloadCsvFile } from '../utils';

export type TableRowProps = {
  uniqueId: number;
  id: string;
  measurementDevice: string;
  aggregationInterval: string;
  aggregationIntervalType: string;
  start: string;
  end: string;
  parameters: string;
  entityId: number;
  usedParameters: { id: number; label: string; value: string }[];
};

export const tsoIds = {
  1: '50Hertz',
  2: 'Amprion',
  3: 'Tennet',
  4: 'TransnetBW',
};

const StyledIconButtonTsoPq = styled(IconButtonTsoPq, {
  shouldForwardProp: (prop) => !['activeEdit'].includes(prop as string),
})(({ activeEdit = false }: { activeEdit: boolean }) => {
  return {
    background: activeEdit
      ? tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_pressed_lt
      : 'transparent',
  };
});

const StyledTsoIndicator = styled('span', {
  shouldForwardProp: (prop) => !['tsoId'].includes(prop as string),
})(({ tsoId }: { tsoId: number }) => {
  const currentTso = tsoIds[`${tsoId}` as keyof typeof tsoIds] ?? 'defaultInactive';

  return {
    height: '1rem',
    width: '1rem',
    background: colorMap[currentTso as keyof typeof colorMap].active,
    display: 'flex',
    margin: 'auto 0',
    borderRadius: '50%',
  };
});

const StyledDeviceNameContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'row',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8,
  };
});

const MeasurementVisualizationAppliedParametersTable = ({
  tableRows = [],
}: {
  tableRows: measurementVisualizationTableRowProps[] | [];
}) => {
  dayjs.extend(utc);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<GridRowId>>(new Set());
  const activeTableRows = useAppSelector((state) => state.measurementVisualization.activeTableRows) ?? [];
  const editingRow = useAppSelector((state) => state.measurementVisualization.editingRow) ?? undefined;
  const [triggerCsvExport] = useLazyPostMeasurementGetCsvExportQuery();

  const dispatch = useDispatch();

  const handleRowClick = (params: any) => {
    if (!params.row.id) return;
    const id = params.id;
    const row = tableRows.find((tableRow) => tableRow.uniqueId === id);

    setSelectedRowIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        row && dispatch(removeActiveTableRow(row.uniqueId));
        newSet.delete(id);
      } else {
        row &&
          dispatch(
            insertActiveTableRow({
              ...row,
              parameters: row.usedParameters.map((parameter) => parameter?.value),
              order: activeTableRows.length + 1,
            })
          );
        newSet.add(id);
      }
      return newSet;
    });
  };


  const handleEdit = (params: any) => {
    dispatch(setEditingRow(params.row.uniqueId));
  };

  const handleDelete = (params: any) => {
    dispatch(removeTableRow(params.row.uniqueId));
    dispatch(removeActiveTableRow(params.row.uniqueId));
  };

  const handleDuplicate = (params: any) => {
    const duplicatedRow = params.row;
    dispatch(
      upsertTableRow({
        ...duplicatedRow,
        uniqueId: `${duplicatedRow.id}-${Date.now()}`,
      })
    );
  };

  const handleDownload = async (params: any) => {
    const row = params.row;
    try {
      const response = await triggerCsvExport(csvParams(row));
      downloadCsvFile(response.data as string, row);
    } catch (error) {
      console.error(error);
    }
  };

  const getRowClassName = (params: any) => {
    return selectedRowIds.has(params.id) ? 'selected-row' : '';
  };

  const columns: GridColDef[] = [
    {
      field: 'chartNumber',
      renderHeader: () => <FontAwesomeIcon icon={faChartLine} />,
      width: 50,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
      sortable: false,
      renderCell: (params) => {
        return (
          <StyledOrderNumber>{activeTableRows.find((row) => row.uniqueId === params.id)?.order}</StyledOrderNumber>
        );
      },
    },
    {
      field: 'measurementDevice',
      headerName: t('measurementVisualization.table.measurementDevice'),
      width: 550,
      flex: 1,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
      renderCell: (params) => {
        return (
          <StyledDeviceNameContainer>
            {params.row.id && <StyledTsoIndicator tsoId={params.row.tsoId} />}
            {params.row.measurementDevice}
          </StyledDeviceNameContainer>
        );
      },
    },
    {
      field: 'aggregationInterval',
      headerName: t('measurementVisualization.table.aggregationInterval'),
      width: 185,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },
    {
      field: 'start',
      headerName: t('measurementVisualization.table.start'),
      width: 225,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },
    {
      field: 'end',
      headerName: t('measurementVisualization.table.end'),
      width: 225,
      headerClassName: 'customBackgroundHeader',
      resizable: true,
    },

    {
      field: 'actions',
      headerName: t('measurementVisualization.table.action'),
      width: 165,
      headerClassName: 'customBackgroundHeader',
      sortable: false,
      filterable: false,
      resizable: true,
      renderCell: (params: any) => (
        <StyledEditIconWrapper>
          <StyledIconButtonTsoPq
            sizeVariant="s"
            buttonType="inputIconPrimary"
            icon={faPenToSquare}
            activeEdit={editingRow === params.row.uniqueId}
            onClick={(event) => {
              event.stopPropagation();
              handleEdit(params);
            }}
            tooltip={t('general.tooltip.editRow')}
          />
          <IconButtonTsoPq
            sizeVariant="s"
            buttonType="inputIconPrimary"
            icon={faTrash}
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(params);
            }}
            tooltip={t('general.tooltip.removeRow')}
          />
          {params.row.measurementDevice && (
            <IconButtonTsoPq
              sizeVariant="s"
              buttonType="inputIconPrimary"
              icon={faCopy}
              onClick={(event) => {
                event.stopPropagation();
                handleDuplicate(params);
              }}
              tooltip={t('general.tooltip.duplicateRow')}
            />
          )}
          {params.row.measurementDevice && (
            <IconButtonTsoPq
              sizeVariant="s"
              buttonType="inputIconPrimary"
              icon={faFileArrowDown}
              onClick={(event) => {
                event.stopPropagation();
                handleDownload(params);
              }}
              tooltip={t('general.tooltip.export')}
            />
          )}
        </StyledEditIconWrapper>
      ),
    },
  ];

  return (
    <StyledDataGrid
      rows={tableRows}
      columns={columns}
      disableRowSelectionOnClick
      disableColumnReorder
      disableColumnPinning
      hideFooterSelectedRowCount
      isCellEditable={() => false}
      hideFooter
      columnHeaderHeight={37}
      getRowHeight={() => 47}
      getRowId={(row) => row.uniqueId}
      showCellVerticalBorder
      slots={{
        columnSortedAscendingIcon: customSortIconAsc,
        columnSortedDescendingIcon: customSortIconDesc,
      }}
      disableColumnMenu
      onRowClick={handleRowClick}
      disableRowSelectionExcludeModel
      rowSelectionModel={{ type: 'include', ids: selectedRowIds }}
      onRowSelectionModelChange={() => { }}
      getRowClassName={getRowClassName}
      localeText={{
        noRowsLabel: t('general.grid.noRows'),
        noResultsOverlayLabel: t('general.grid.noMatchingRows'),
      }}
      sx={{
        maxHeight: 5 * 47 + 36, //set height of 5 rows + header in px
        overflowY: 'auto',
        '& .MuiDataGrid-scrollbar': {
          height: 5 * 47,
          overflowX: 'hidden',
        },
      }}
    />
  );
};

export default MeasurementVisualizationAppliedParametersTable;
